// Lifting the calendar into the type system
// TODO: Template string literal for yearless dates?

export interface YearlessDate {
  month: MoY // TODO: Use the real types?
  day: DoM
} // TODO: Can I exclue the invalid combinations like below

export const jan = { mm: "01", days: 31, name: "January" } as const;
export const feb = { mm: "02", days: 29, name: "February" } as const;
export const mar = { mm: "03", days: 31, name: "March" } as const;
export const apr = { mm: "04", days: 30, name: "April" } as const;
export const may = { mm: "05", days: 31, name: "May" } as const;
export const jun = { mm: "06", days: 30, name: "June" } as const;
export const jul = { mm: "07", days: 31, name: "July" } as const;
export const aug = { mm: "08", days: 31, name: "August" } as const;
export const sep = { mm: "09", days: 30, name: "September" } as const;
export const oct = { mm: "10", days: 31, name: "October" } as const;
export const nov = { mm: "11", days: 30, name: "November" } as const;
export const dec = { mm: "12", days: 31, name: "December" } as const;

export const months = [jan,feb,mar,apr,may,jun,jul,aug,sep,oct,nov,dec] as const;
export type Month = typeof months[number];
export type MoY = typeof months[number]['mm']; // TODO: rename mm => moy?

export function isMoY(mm: string): mm is MoY {
  return months.find(mon => mon.mm === mm) != null;
}

export function moyIndex(moy: MoY): number { // TODO range 0..11
  const index = months.findIndex(m => m.mm === moy);
  if (index < 0) { throw new Error(`invalid MoY: ${moy}`); }
  return index;
}

export const moyMonth = (moy: MoY): Month => months[moyIndex(moy)];
export const moyDisplay = (moy: MoY): string => moyMonth(moy).name;

export function moyNext(moy: MoY): MoY {
  const next = moyIndex(moy) + 1;
  return next < months.length ? months[next].mm : '01';
}

export function moyPrev(moy: MoY): MoY {
  const prev = moyIndex(moy) - 1;
  return prev < 0 ? '12' : months[prev].mm;
}

const doms = [
  '01','02','03','04','05','06','07','08','09','10',
  '11','12','13','14','15','16','17','18','19','20',
  '21','22','23','24','25','26','27','28','29','30',
  '31' ] as const;

export type DoM = typeof doms[number];

export function domIndex(dom: DoM): number { // TODO range 0..30
  const index = doms.indexOf(dom);
  if (index < 0) { throw new Error(`invalid DoM: ${dom}`); }
  return index;
}

// Note this doesn't take the month into account
export function domNext(dom: DoM): DoM {
  const next = domIndex(dom) + 1;
  return next < doms.length ? doms[next] : '01';
}

// Note this doesn't take the month into account
export function domPrev(dom: DoM): DoM {
  const prev = domIndex(dom) - 1;
  return prev >= 0 ? doms[prev] : '31';
}

export type DoY = Exclude<`${MoY}-${DoM}`,
  '02-30'|'02-31'|'04-31'|'06-31'|'09-31'|'11-31'>

export function doyDisplay(doy: DoY): string {
  const [moy, dom] = doy.split('-') as [MoY, DoM];
  return `${moyMonth(moy).name} ${dom}`;
}

export function doyNext(doy: DoY): DoY {
  let [moy, dom] = doy.split('-') as [MoY, DoM];
  dom = domNext(dom);
    if (dom >= `${moyMonth(moy).days}`) {
    dom = '01';
  }
  if (dom === '01') {
    moy = moyNext(moy);
  }
  return `${moy}-${dom}` as DoY;
}

export function doyPrev(doy: DoY): DoY {
  let [moy, dom] = doy.split('-') as [MoY, DoM];
  dom = domPrev(dom);
  if (dom === '31') {
    moy = moyPrev(moy);
    dom = doms[moyMonth(moy).days-1];
  }
  return `${moy}-${dom}` as DoY;
}

export const sun = { dow: 1, name: "Sunday" } as const;
export const mon = { dow: 2, name: "Monday" } as const;
export const tue = { dow: 3, name: "Tuesday" } as const;
export const wed = { dow: 4, name: "Wednesday" } as const;
export const thu = { dow: 5, name: "Thursday"} as const;
export const fri = { dow: 6, name: "Friday" } as const;
export const sat = { dow: 7, name: "Saturday" } as const;

export const weekdays = [sun,mon,tue,wed,thu,fri,sat] as const;
export type Weekday = typeof weekdays[number]; // TODO DayOfWeek?

// YearlessDate = { mm: MoY, dd: DoM } or { moy: MoY, dom: DoM }
// Yearless

// TODO: Make this a type
export function parseYearMonthDay(s: string): { year: string, month: MoY, day: string } {
  const [ year, month, day ] = s.split("-");
  if (!isMoY(month)) {
    throw new Error(`invalid year-mm-dd: ${s}`);
  }
  return { year, month, day };
}

export function yearMonDay(d: {year: number, month: MoY, day: string}) {
  return `${d.year}-${d.month}-${d.day}`;
}

export type CalMonth = {
  month: Month
  days: CalDay[]
  weeks: CalWeek[]
}

export type CalWeek = {
  wom: number // TODO: 1|2|3|4|5|6 // week-of-month
  days: CalDay[]
  // TODO: woy?
}

export type CalDay = {
  doy: DoY
  dom: DoM
  dow: number // TODO 1|2|3|4|5|6|7 day-of-week
}

const toWeeks = (numDays: number) => chunk(Array.from({length: numDays}, (_, i) => i+1), 7);

export function yearlessMonths(): CalMonth[] { // TODO: Specific for the full year
  return months.map(m => {
    const days = Array.from({length: m.days}, (_, i) => {
      const dom = `${i+1}`.padStart(2, '0') as DoM;
      return { dom, doy: `${m.mm}-${dom}` as DoY };
    });
    const weeks: CalWeek[] = chunk(days, 7).map((week, i) => {
      const days = week.map((d, j) => ({ ...d, dow: j+1 }));
      return { days, wom: i+1 };
    });
    return { month: m, weeks, days: weeks.flatMap(w => w.days) };
  })
}

export function yearless() {
  return months.map(m => {
    // TODO: Make a week object
    return { ...m, weeks: toWeeks(m.days).map(w => {
        return w.map(day => {
          // TODO: mmdd instead of date?
          const date = `${m.mm}-${day.toString().padStart(2, '0')}`
          return { day, date, week: Math.floor((day+6)/7) };
        });
      })
    };
  });
};

function chunk<T>(arr: T[], n: number): T[][] {
  const o = {length: Math.ceil(arr.length / n)};
  return Array.from(o, (_, i) => arr.slice(i*n, (i+1)*n));
}

// TODO: Start defining these...
// const parts = {
//   // SUBSECOND
//   // %f - microseconds as a decimal number [000000, 999999].
//   // %L - milliseconds as a decimal number [000, 999].
//   'SECOND': [
//     [ '%S', 'second as a decimal number [00,61].' ],
//   ],
//   'MINUTE': [
//     [ '%M', 'minute as a decimal number [00,59].' ],
//   ],
//   'HOUR': [
//     [ '%H', 'hour (24-hour clock) as a decimal number [00,23].' ],
//     [ '%I', 'hour (12-hour clock) as a decimal number [01,12].' ],
//     [ '%p', 'either AM or PM.*' ],
//   ],
//   'WEEKDAY': [ // DAY-OF-WEEK for consistency?
//     [ '%a', 'abbreviated weekday name.*' ],
//     [ '%A', 'full weekday name.*' ],
//     [ '%u', 'Monday-based (ISO 8601) weekday as a decimal number [1,7].' ],
//     [ '%w', 'Sunday-based weekday as a decimal number [0,6].' ],
//   ],
//   'DAY-OF-MONTH': [
//     [ '%d', 'zero-padded day of the month as a decimal number [01,31].' ],
//     [ '%e', 'space-padded day of the month as a decimal number [ 1,31]; equivalent to %_d.' ],
//   ],
//   'DAY-OF-YEAR': [
//     // (yearless-date sort-of but leap years get it wrong...)
//     // There's a mapping where this works with yearless. (e.g. my own %J)
//     [ '%j', 'day of the year as a decimal number [001,366].' ],
//   ],
//   'WEEK-OF-YEAR': [
//     // (does this work with "yearless"? sort of)
//     [ '%U', 'Sunday-based week of the year as a decimal number [00,53].' ],
//     [ '%V', 'ISO 8601 week of the year as a decimal number [01, 53].' ],
//     [ '%W', 'Monday-based week of the year as a decimal number [00,53].' ],
//   ],
//   'MONTH': [
//     [ '%b', 'abbreviated month name.*' ],
//     [ '%B', 'full month name.*' ],
//     [ '%m', 'month as a decimal number [01,12].' ],
//   ],
//   'QUARTER': [
//     [ '%q', 'quarter of the year as a decimal number [1,4].' ],
//   ],
//   'YEAR': [
//     [ '%g', 'ISO 8601 week-based year without century as a decimal number [00,99].' ],
//     [ '%G', 'ISO 8601 week-based year with century as a decimal number.' ],
//     [ '%y', 'year without century as a decimal number [00,99].' ],
//     [ '%Y', 'year with century as a decimal number, such as 1999.' ],
//   ],
//   'DATETIME': [
//     [ '%c', 'the locale’s date and time, such as %x, %X.*' ],
//   ],
//   'DATE': [
//     [ '%x', 'the locale’s date, such as %-m/%-d/%Y.*' ],
//   ],
//   'TIME': [ // does this need full-second accuracy?
//     [ '%X', 'the locale’s time, such as %-I:%M:%S %p.*' ],
//   ],
//   'UNIX-EPOCH': [
//     [ '%Q', 'milliseconds since UNIX epoch.' ],
//     [ '%s', 'seconds since UNIX epoch.' ],
//   ],
//   'TIMEZONE': [ // supported by anything?
//     [ '%Z', 'time zone offset, such as -0700, -07:00, -07, or Z.' ],
//   ],
//   'LITERAL': [ // supported by everything
//     [ '%%', 'a literal percent sign (%).' ],
//   ],
// };

// // Part combinations for reuse below
// const MD = [ 'QUARTER', 'MONTH', 'DAY-OF-MONTH' ]; // FIXED-DOY, FIXED-WOY, FIXED-DOM, FIXED-WOM
// const YMD = [ ...MD, 'YEAR', 'DATE', 'WEEK-OF-YEAR', 'DAY-OF-YEAR', 'WEEKDAY' ];

// // TODO: This is overkill, I don't need any of this...
// // date-hms
// // date-hm
// // date-h

// const kinds = {
//   "date-hm": {
//     parts: [ ...YMD, 'HOUR', 'MINUTE' ],
//     specifiers: [
//       "%Y-%a-%d %H:%M",  // 2023-apr-23 19:36
//       "%Y-%m-%d %-I:%M %p", // 2023-04-23 7:36 PM
//     ],
//   },
// }

// // limit to what I need for now

// // the "kind" wholly describes the specificity, even if there are different supported formats
// // generally go from least specific to most specific in my formats

// const formats = {
//   // TODO: Better name for "yearless" - these parse to year 1900
//   // Matchers are mutually exclusive so order doesn't matter. Need at least one
//   // specifier, the first is assumed to be the "canonical" format
//   datetime: [ // Doesn't require full second accuracy for this one (do we care?)
//     "%Y-%a-%d %H:%M %p",  // 2023-apr-23 19:36
//     "%Y-%m-%d %-I:%M %p", // 2023-04-23 7:36 PM
//   ],
//   date: [
//     "%Y-%b-%d", // 2023-apr-23
//     "%Y-%m-%d", // 2023-04-23
//   ],
//   // "week", ["TODO"],
//   month: [
//     "%Y-%b", // 2023-apr
//     "%Y-m" , // 2023-04
//   ],
//   year: [
//     "%Y", // 2023
//   ],
//   // Not really intervals, more like filters
//   "yearless-date": [
//     "%b-%d", // apr-23
//     "%m-%d", // 04-23
//   ],
//   "yearless-month": [
//     "%b", // apr
//     "%B", // April
//   ],
//   // TODO weekday's parse to Jan 1, 1900 regardlesss of day so need special handling
//   // Mostly likely what I'll want these for are inclusive filters...
//   // TODO: I may need to use the d3.timeInterval, e.g. d3.timeMondays
//   // { kind: "weekday", specifier: "%a" },                   // fri
//   // { kind: "weekday", specifier: "%A" },                   // Friday

//   // Other Possible combinations combinations
//   // month-weekday
//   // month-week
//   // year-weekday
//   // day-of-year (leep year?) force 3-digit number or d-prefix
//   // holiday-easter
//   // seasons
//   // quarters

//   // Maybe some interesting ideas?
//   // https://medium.com/@peterflom/are-dates-nominal-ordinal-interval-or-ratio-ef567e10884
// };

// // There's an interesting argument for parsing yearless months - e.g. "April"
// //  - "this year" (not necessarily by rounding)
// //  - "nearest" (rounding)
// //  - "next" (what if it's currently April)
// //  - "last" (what if it's currently April)
// //  - "any" (an arbitrary April)
// //  - "all" (all April times)

// // Having a next and prev built-in that operates at the level of the kind

// // For these "yearless" timeframes, we need an additional qualifier from the
// // above list to figure it out.

// // Similar for parsing a yearless weekday - e.g. "Friday" except relative to weeks

// // For some things to work I may need multiple sentinels
// // TODO: Need to be careful with the 1900 sentinal since it's not a leap year
// // Maybe instead prefix the yearless dates with my sentinel and parse that way?
// const yearless = 8888; // TODO: sentinel year, it's a leap year

// // TODO: What is the name of this collection? Mapping
// const matchers = Object.entries(formats).map(([ kind, specifiers ]) => {
//   // TODO: Is this the invariant I'm trying to ensure
//   // I also want to have "canonical" formats when there are multiple
//   // parse(str).when === parse(format(parse(str).when)).when
//   let parsers = specifiers.map(specifier => {
//     if (kind.startsWith("yearless-")) {
//       // TODO: This is janky...
//       const yeared = d3.timeParse(`%Y-${specifier}`)
//       return s => yeared(`${yearless}-${s}`);
//     } else {
//       return d3.timeParse(specifier);
//     }
//   });

//   return { kind, format: d3.timeFormat(specifiers[0]), parse: s => {
//     return parsers.map(p => p(s)).find(date => date);
//   }};
// });

// // More like regex for dates, datex[p], dateish
// function parseDateTime(str) {
//   // And then one other function for if a dateish includes another dateish _or_ an actual Date object
//   // Alternatively I can have a "filter" function that takes the dateish and a dateish|Date
//   // return { kind, cananonical, when?  } | null
//   for (const { kind, parse, format } of matchers) {
//     const when = parse(str);
//     if (when != null) {
//       const dateish = { kind, canonical: format(when) };
//       if (when.getFullYear() !== yearless) {
//         // TODO: Hide the when, expose a format and complain if using an incorrect format specifier
//         dateish.when = when;
//       }
//       return dateish;
//     }
//   }
//   return null;
// }

// // https://github.com/d3/d3-time-format#locale_format

// // May need to treat Jan 01, 1900 lists special
// // And maybe for the "yearless ones override the default year to 1899 to differentiate"
